import React, { FC, useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useObserver } from '../../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../../Legacy';
import { CommandDispatcher } from '../../../../concepts/commands/CommandDispatcher';
import { Component, ObjectManager } from '../../../../concepts/objects';
import { useWindowEvent } from '../../../../platform/Hook';
import { Matrix } from '../../../../platform/geom/Matrix';
import { KeyExpression } from '../../../../platform/input/KeyExpression';
import { usePrompt } from '../../../../ui';
import { useResolve } from '../../../Container';
import { AdapterInfo } from '../../AdapterInfo';
import { Camera } from '../../Camera';
import { EditorSession } from '../../EditorSession';
import { PointerInfo } from '../../PointerInfo';
import { beginMutating, duplicate } from '../../commands/ManipulationCommands';
import { Physical, RelativeTo } from '../../entities/Physical';
import { UIField } from '../../entities/UIField';
import { useComponentObserver } from '../../visualization/ComponentObserver';
import { SimpleOrigin } from '../../visualization/Origin';

const nextExpr = KeyExpression.parse('TAB');
const prevExpr = KeyExpression.parse('TAB+SHIFT');
const closeOutExpr = KeyExpression.parse('CTRL+ENTER');

const useDefaultField = (subject: Physical, fields: readonly UIField[]) => {
    const camera = useResolve(Camera);
    const session = useResolve(EditorSession);
    return useMemo(() => {
        const mpt = camera.toModel(session.get(PointerInfo).position);
        const rpt = subject.toRelative(mpt);
        return fields.find(f => f.bounds.contains(rpt)) ?? fields[0];
    }, [subject]);
};

const AdapterMount: LFC<{ object: Physical }> = ({ object, children }) => {
    const session = useResolve(EditorSession);
    const promptRef = usePrompt({
        name: 'adapter',
        ignoreEnter: true,
        onAction: () => session.update(AdapterInfo, AdapterInfo.empty)
    });
    if (!object) return null;
    const transform = object.transform(RelativeTo.Model);
    const size = object.size();
    const style = {
        position: 'absolute' as const,
        width: size.w,
        height: size.h,
        transformOrigin: '0 0',
        transform: transform.toCSS(),
        pointerEvents: 'none' as const,
    };
    return <div ref={promptRef} style={style}>
        {children}
    </div>;
};

const FieldMount = styled.div`
    position: absolute;
    transform-origin: 0 0;
    pointer-events: all;
`;

const FieldInstance = ({ active, field }: { active?: boolean, field: UIField }) => {
    const FieldUI = field.ui;
    return <FieldUI active={active} field={field} />;
};

const TriggerCommitOnUnmount = React.memo<{ subject: Physical }>(({ subject }) => {

    const dispatch = useResolve(CommandDispatcher);
    const objects = useResolve(ObjectManager);

    useEffect(() => () => {
        if (!subject.alive) return;
        // If object type has a commit command, invoke it, otherwise noop.
        const { commit } = Component.describe(subject);
        if (commit) dispatch(commit, [subject.id]);
        else objects.update(u => u.noop());
    }, [subject]);

    return null;
});

export const FieldAdapter: FC<{ info: AdapterInfo }> = ({ info }) => {

    const dispatch = useResolve(CommandDispatcher);
    const objects = useResolve(ObjectManager);
    const session = useResolve(EditorSession);

    const subject = useMemo(() => objects.getMaybe(info.target, Physical), [info.target]);
    useComponentObserver(subject);
    useObserver(useResolve(Camera));

    const fields = subject?.uiFields ?? [];
    const defaultField = useDefaultField(subject, fields);
    const active = info.name || defaultField?.name;

    const switchObject = useCallback((mode: string) => {
        dispatch(duplicate, [mode, [subject.id]]);
        dispatch(beginMutating);
    }, [subject]);

    const switchField = useCallback((by: number) => {
        if (!active) return;
        const afi = fields.findIndex(x => x.name === active);
        const nfi = afi + by;
        if (nfi < 0) {
            // Hmm
        }
        else if (nfi >= fields.length) {
            switchObject('tab');
        }
        else {
            session.apply(AdapterInfo, x => ({ ...x, name: fields[nfi].name }));
        }
    }, [active, fields, switchObject]);

    useWindowEvent('keydown', useCallback(e => {
        if (prevExpr.matches(e)) {
            e.preventDefault();
            switchField(-1);
        }
        else if (nextExpr.matches(e)) {
            e.preventDefault();
            switchField(+1);
        }
        else if (closeOutExpr.matches(e)) {
            e.preventDefault();
            switchObject('newline');
        }
    }, [switchField, switchObject]));

    return <SimpleOrigin>
        <AdapterMount object={subject}>
            {fields.map(f => (
                <FieldMount key={f.name} data-testid={f.name} style={{ ...f.bounds.toCSS(), transform: (f.offset ?? Matrix.identity).toCSS() }}>
                    <FieldInstance active={active === f.name} field={f} />
                </FieldMount>
            ))}
        </AdapterMount>
        <TriggerCommitOnUnmount subject={subject} />
    </SimpleOrigin>;
};
